<script lang="ts">
  import type { StreamInfo } from '../lib/types';
  import { patchStreamLabel } from '../lib/api';
  import Player from './Player.svelte';

  type Props = {
    stream: StreamInfo;
    archiveMode?: boolean;
    overrideStreamName?: string;
    overrideLabel?: string;
    rate?: number;
    /** When true, cell is paused (off-screen / non-active in fullscreen). */
    paused?: boolean;
    onarchiveclick?: () => void;
    onclipclick?: () => void;
    onfullscreenclick?: () => void;
    onvideo?: (v: HTMLVideoElement) => void;
  };

  let {
    stream,
    archiveMode = false,
    overrideStreamName,
    overrideLabel,
    rate = 1,
    paused = false,
    onarchiveclick,
    onclipclick,
    onfullscreenclick,
    onvideo,
  }: Props = $props();

  let stat = $state('…');
  let statBad = $state(false);
  let label = $state(stream.label || stream.name);
  let editingLabel = $state(false);
  let labelInput: HTMLInputElement | undefined = $state();

  $effect(() => {
    label = overrideLabel ?? stream.label ?? stream.name;
  });

  async function commitLabelEdit(commit: boolean): Promise<void> {
    if (!labelInput) return;
    const newVal = labelInput.value.trim();
    editingLabel = false;
    if (!commit || newVal === label) return;
    try {
      await patchStreamLabel(stream.name, newVal);
      label = newVal;
    } catch (e) {
      alert('Не сохранилось: ' + (e instanceof Error ? e.message : e));
    }
  }

  function onLabelDblClick(e: MouseEvent): void {
    if (archiveMode) return;
    e.stopPropagation();
    editingLabel = true;
    queueMicrotask(() => {
      labelInput?.focus();
      labelInput?.select();
    });
  }

  function onClick(e: MouseEvent): void {
    if (editingLabel) return;
    const t = e.target as HTMLElement;
    if (t.closest('.archive-btn')) {
      e.stopPropagation();
      onarchiveclick?.();
      return;
    }
    if (t.closest('.clip-btn')) {
      e.stopPropagation();
      onclipclick?.();
      return;
    }
    onfullscreenclick?.();
  }
</script>

<div
  class="cell"
  class:archive-mode={archiveMode}
  onclick={onClick}
  onkeydown={(e) => {
    if (e.key === 'Enter' || e.key === ' ') onClick(e as unknown as MouseEvent);
  }}
  role="button"
  tabindex="0"
>
  <Player
    streamName={overrideStreamName ?? stream.name}
    {rate}
    {paused}
    onstatus={(t, bad) => {
      stat = t;
      statBad = bad ?? false;
    }}
    {onvideo}
  />
  <div class="label" class:editing={editingLabel}>
    {#if editingLabel}
      <input
        bind:this={labelInput}
        value={label}
        maxlength="64"
        onclick={(e) => e.stopPropagation()}
        onkeydown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commitLabelEdit(true);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            commitLabelEdit(false);
          }
        }}
        onblur={() => commitLabelEdit(true)}
      />
    {:else}
      <span ondblclick={onLabelDblClick} role="button" tabindex="0"
            onkeydown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLElement).dispatchEvent(new MouseEvent('dblclick'));
            }}
      >{label}</span>
    {/if}
  </div>
  {#if !archiveMode}
    <button class="clip-btn" type="button" title="Скачать фрагмент">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
      </svg>
    </button>
    <button class="archive-btn" type="button" title="Архив">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
    </button>
  {/if}
  <div class="stat" class:bad={statBad}>{stat}</div>
</div>

<style>
  .cell {
    position: relative;
    overflow: hidden;
    background: var(--bg-cell);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    aspect-ratio: 16/9;
    cursor: pointer;
    transition:
      transform 0.15s,
      border-color 0.15s;
  }
  .cell:hover {
    border-color: rgba(127, 127, 127, 0.25);
  }
  .cell.archive-mode {
    border-color: rgba(255, 165, 0, 0.6);
  }
  .label {
    position: absolute;
    top: 8px;
    left: 10px;
    padding: 3px 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 500;
    color: #fff;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    pointer-events: auto;
  }
  .label.editing {
    background: rgba(0, 113, 227, 0.6);
    padding: 0;
  }
  .label input {
    background: transparent;
    border: 0;
    outline: none;
    color: #fff;
    font: inherit;
    padding: 3px 8px;
    min-width: 60px;
    max-width: 200px;
  }
  .archive-mode .label {
    background: rgba(255, 140, 0, 0.7);
  }
  .archive-btn,
  .clip-btn {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 28px;
    height: 28px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.5);
    color: #fff;
    display: none;
    place-items: center;
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
  }
  .clip-btn {
    right: 42px;
  }
  .cell:hover .archive-btn,
  .cell:hover .clip-btn {
    display: grid;
  }
  .archive-btn svg,
  .clip-btn svg {
    width: 14px;
    height: 14px;
  }
  .stat {
    position: absolute;
    bottom: 8px;
    right: 10px;
    padding: 2px 7px;
    border-radius: 999px;
    font-size: 10px;
    font-family: ui-monospace, 'SF Mono', Menlo, monospace;
    color: rgba(255, 255, 255, 0.85);
    background: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    pointer-events: none;
    font-variant-numeric: tabular-nums;
  }
  .stat.bad {
    color: #ff7a73;
  }
</style>
